// geekCategories.js - Enhanced geek culture category detection

// Define the main categories and subcategories
export const CATEGORIES = {
  COMICS: {
    id: 'comics',
    name: 'Comics',
    subcategories: {
      MARVEL: { id: 'marvel', name: 'Marvel' },
      DC: { id: 'dc', name: 'DC' },
      INDIE: { id: 'indie', name: 'Independent Comics' },
      MANGA: { id: 'manga', name: 'Manga' }
    }
  },
  ANIME: {
    id: 'anime',
    name: 'Anime',
    subcategories: {
      SHOUNEN: { id: 'shounen', name: 'Shounen' },
      SHOUJO: { id: 'shoujo', name: 'Shoujo' },
      SEINEN: { id: 'seinen', name: 'Seinen' },
      ISEKAI: { id: 'isekai', name: 'Isekai' },
      MECHA: { id: 'mecha', name: 'Mecha' }
    }
  },
  GAMES: {
    id: 'games',
    name: 'Games',
    subcategories: {
      VIDEOGAMES: { id: 'videogames', name: 'Video Games' },
      TABLETOP: { id: 'tabletop', name: 'Tabletop & Board Games' },
      RPG: { id: 'rpg', name: 'Role-Playing Games' }
    }
  },
  MOVIES: {
    id: 'movies',
    name: 'Movies',
    subcategories: {
      SUPERHERO: { id: 'superhero_movies', name: 'Superhero Movies' },
      SCIFI: { id: 'scifi_movies', name: 'Sci-Fi Movies' },
      FANTASY: { id: 'fantasy_movies', name: 'Fantasy Movies' },
      ANIMATION: { id: 'animation', name: 'Animated Movies' }
    }
  },
  TV: {
    id: 'tv',
    name: 'TV Shows',
    subcategories: {
      SUPERHERO_TV: { id: 'superhero_tv', name: 'Superhero TV Shows' },
      SCIFI_TV: { id: 'scifi_tv', name: 'Sci-Fi TV Shows' },
      FANTASY_TV: { id: 'fantasy_tv', name: 'Fantasy TV Shows' },
      ANIMATION_TV: { id: 'animation_tv', name: 'Animated Series' }
    }
  },
  CHARACTERS: {
    id: 'characters',
    name: 'Characters',
    subcategories: {
      HEROES: { id: 'heroes', name: 'Heroes' },
      VILLAINS: { id: 'villains', name: 'Villains' },
      SUPPORTING: { id: 'supporting', name: 'Supporting Characters' }
    }
  }
};

// Mapping of franchises to categories
const franchiseCategoryMap = {
  // Marvel franchises
  'marvel': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'avengers': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'x-men': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'spider-man': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'iron man': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'captain america': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'thor': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'hulk': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'black widow': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'doctor strange': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'black panther': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  'fantastic four': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MARVEL.id],
  
  // DC franchises
  'dc': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'batman': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'superman': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'wonder woman': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'flash': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'aquaman': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'green lantern': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'justice league': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'teen titans': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  'suicide squad': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.DC.id],
  
  // Indie comics
  'walking dead': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'spawn': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'hellboy': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'invincible': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'saga': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'watchmen': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'sandman': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'preacher': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  'boys': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.INDIE.id],
  
  // Manga
  'manga': [CATEGORIES.COMICS.id, CATEGORIES.COMICS.subcategories.MANGA.id],
  'one piece': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'naruto': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'dragon ball': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'bleach': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'attack on titan': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'my hero academia': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'demon slayer': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'jujutsu kaisen': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'hunter x hunter': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  'one punch man': [CATEGORIES.COMICS.subcategories.MANGA.id, CATEGORIES.ANIME.id],
  
  // Anime
  'anime': [CATEGORIES.ANIME.id],
  'studio ghibli': [CATEGORIES.ANIME.id, CATEGORIES.MOVIES.subcategories.ANIMATION.id],
  'fullmetal alchemist': [CATEGORIES.ANIME.id, CATEGORIES.COMICS.subcategories.MANGA.id],
  'death note': [CATEGORIES.ANIME.id, CATEGORIES.COMICS.subcategories.MANGA.id],
  'evangelion': [CATEGORIES.ANIME.id, CATEGORIES.ANIME.subcategories.MECHA.id],
  'gundam': [CATEGORIES.ANIME.id, CATEGORIES.ANIME.subcategories.MECHA.id],
  'sailor moon': [CATEGORIES.ANIME.id, CATEGORIES.ANIME.subcategories.SHOUJO.id],
  'pokemon anime': [CATEGORIES.ANIME.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  'digimon': [CATEGORIES.ANIME.id],
  'yugioh': [CATEGORIES.ANIME.id, CATEGORIES.GAMES.id],
  
  // Games
  'video game': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  'video games': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  'board game': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.TABLETOP.id],
  'tabletop': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.TABLETOP.id],
  'rpg': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.RPG.id],
  'role-playing game': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.RPG.id],
  'dungeons and dragons': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.RPG.id],
  'final fantasy': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  'pokemon game': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  'zelda': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  'mario': [CATEGORIES.GAMES.id, CATEGORIES.GAMES.subcategories.VIDEOGAMES.id],
  
  // Movies
  'mcu': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.SUPERHERO.id],
  'marvel cinematic': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.SUPERHERO.id],
  'dceu': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.SUPERHERO.id],
  'dc extended': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.SUPERHERO.id],
  'star wars': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.SCIFI.id],
  'star trek': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.SCIFI.id],
  'lord of the rings': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.FANTASY.id],
  'harry potter': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.FANTASY.id],
  'pixar': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.ANIMATION.id],
  'anime movie': [CATEGORIES.MOVIES.id, CATEGORIES.MOVIES.subcategories.ANIMATION.id],
  
  // TV Shows
  'tv show': [CATEGORIES.TV.id],
  'television series': [CATEGORIES.TV.id],
  'netflix series': [CATEGORIES.TV.id],
  'game of thrones': [CATEGORIES.TV.id, CATEGORIES.TV.subcategories.FANTASY_TV.id],
  'mandalorian': [CATEGORIES.TV.id, CATEGORIES.TV.subcategories.SCIFI_TV.id],
  'stranger things': [CATEGORIES.TV.id, CATEGORIES.TV.subcategories.SCIFI_TV.id],
  'walking dead show': [CATEGORIES.TV.id],
  'arrow': [CATEGORIES.TV.id, CATEGORIES.TV.subcategories.SUPERHERO_TV.id],
  'flash tv': [CATEGORIES.TV.id, CATEGORIES.TV.subcategories.SUPERHERO_TV.id],
  'daredevil show': [CATEGORIES.TV.id, CATEGORIES.TV.subcategories.SUPERHERO_TV.id],
  
  // Characters
  'character': [CATEGORIES.CHARACTERS.id],
  'hero': [CATEGORIES.CHARACTERS.id, CATEGORIES.CHARACTERS.subcategories.HEROES.id],
  'villain': [CATEGORIES.CHARACTERS.id, CATEGORIES.CHARACTERS.subcategories.VILLAINS.id],
  'superhero': [CATEGORIES.CHARACTERS.id, CATEGORIES.CHARACTERS.subcategories.HEROES.id],
  'supervillain': [CATEGORIES.CHARACTERS.id, CATEGORIES.CHARACTERS.subcategories.VILLAINS.id],
  'origin story': [CATEGORIES.CHARACTERS.id],
  'power': [CATEGORIES.CHARACTERS.id],
  'ability': [CATEGORIES.CHARACTERS.id]
};

// Detect categories from a query string
export const detectCategories = (query) => {
  const normalizedQuery = query.toLowerCase();
  const detectedCategories = new Set();
  
  // Check against our franchise-category map
  for (const [franchise, categories] of Object.entries(franchiseCategoryMap)) {
    if (normalizedQuery.includes(franchise)) {
      categories.forEach(category => detectedCategories.add(category));
    }
  }
  
  // If no categories were detected, add a default
  if (detectedCategories.size === 0) {
    detectedCategories.add('general_geek');
  }
  
  return Array.from(detectedCategories);
};

// Generate a system prompt enhancement based on detected categories
export const generateCategoryPrompt = (categories) => {
  let prompt = "Based on the user's query, I'll focus on providing information about: ";
  
  const categoryDescriptions = categories.map(categoryId => {
    // Check if it's a main category
    for (const mainCategory of Object.values(CATEGORIES)) {
      if (mainCategory.id === categoryId) {
        return mainCategory.name;
      }
      
      // Check if it's a subcategory
      for (const subCategory of Object.values(mainCategory.subcategories)) {
        if (subCategory.id === categoryId) {
          return `${subCategory.name} (part of ${mainCategory.name})`;
        }
      }
    }
    
    // Default case
    return categoryId.replace('_', ' ');
  });
  
  prompt += categoryDescriptions.join(', ') + '.';
  
  // Add specific guidelines based on categories
  if (categories.includes(CATEGORIES.COMICS.id)) {
    prompt += " I'll reference comic issues, story arcs, and creators when relevant.";
  }
  
  if (categories.includes(CATEGORIES.ANIME.id)) {
    prompt += " I'll mention relevant episodes, seasons, and studios for anime content.";
  }
  
  if (categories.includes(CATEGORIES.CHARACTERS.id)) {
    prompt += " I'll focus on character details like powers, origins, relationships, and significant story arcs.";
  }
  
  return prompt;
};

// Get visual theme based on detected categories
export const getCategoryTheme = (categories) => {
  // Default theme
  let theme = {
    primaryColor: '#6b5cff', // Default purple
    icon: 'default'
  };
  
  // Set theme based on primary category
  if (categories.includes(CATEGORIES.COMICS.subcategories.MARVEL.id)) {
    theme = {
      primaryColor: '#e23636', // Marvel red
      icon: 'marvel'
    };
  } else if (categories.includes(CATEGORIES.COMICS.subcategories.DC.id)) {
    theme = {
      primaryColor: '#0476F2', // DC blue
      icon: 'dc'
    };
  } else if (categories.includes(CATEGORIES.ANIME.id)) {
    theme = {
      primaryColor: '#FF6B6B', // Anime pink/red
      icon: 'anime'
    };
  } else if (categories.includes(CATEGORIES.GAMES.id)) {
    theme = {
      primaryColor: '#50C878', // Gaming green
      icon: 'games'
    };
  }
  
  return theme;
}; 